import { useActionSheet } from '@expo/react-native-action-sheet'
import { createRef, useState } from 'react'
import { View, ViewProps } from 'react-native'
import { Menu } from 'react-native-paper'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import ReturnAdoptionAction from '@/components/fosters/actions/ReturnAdoptionAction'
import AdoptionForm from '@/components/fosters/adoptions/AdoptionForm'
import Alert from '@/components/shared/Alert'
import Snackbar from '@/components/snackbar/Snackbar'
import UniIconButton from '@/components/unistyles/UniIconButton'
import UniText from '@/components/unistyles/UniText'
import UniTouchableRipple from '@/components/unistyles/UniTouchableRipple'
import PaymentMethod from '@/enums/paymentMethod'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import useUserRoles from '@/hooks/useUserRoles'
import FosterService from '@/services/fosterService'
import findNodeHandle from '@/utils/findNodeHandle'
import formatPhoneNumber from '@/utils/formatPhoneNumber'
import trpc from '@/utils/trpc'

interface Props {
  adoption: {
    id: string
    adoptedByName: string | null
    adoptedByEmail: string | null
    adoptedByPhone: string | null
    adoptedName: string | null
    adoptionDate: Date | null
    returnDate: Date | null
    returnReason: string | null
    paymentMethod: PaymentMethod | null
    userId: string
  }
  disabled: boolean
  fosterName: string
  fosterId: string
}

const AdoptionListItem = ({
  adoption,
  disabled,
  fosterName,
  fosterId,
  ...props
}: Props & ViewProps) => {
  const { isNot } = useBreakpoint()
  const { theme } = useUnistyles()
  const { showActionSheetWithOptions } = useActionSheet()
  const { isUserAdmin } = useUserRoles()
  const { foster, refreshFoster } = FosterService.useFoster(fosterId)
  const [isEditingModalOpen, setIsEditingModalOpen] = useState(false)
  const [isReturnAdoptionModalOpen, setIsReturnAdoptionModalOpen] =
    useState(false)
  const [menuVisible, setMenuVisible] = useState(false)

  const _anchorRef = createRef<any>()

  const editAdoptionMutation = trpc.foster.adoptions.update.useMutation()
  const deleteAdoptionMutation = trpc.foster.adoptions.delete.useMutation()

  const handleDelete = () => {
    const organizationId = foster?.organizationId
    if (!organizationId) {
      Snackbar.error('Foster is still loading. Please try again in a moment.')
      return
    }

    Alert.alert(
      'Delete Adoption Record',
      'Are you sure you want to delete this adoption record? This cannot be undone.',
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => {
            deleteAdoptionMutation.mutate(
              {
                fosterId,
                organizationId,
                id: adoption.id,
              },
              {
                onSuccess: () => {
                  refreshFoster()
                },
                onError: () => {
                  Snackbar.error('There was a problem deleting this adoption record')
                },
              }
            )
          },
        },
      ]
    )
  }

  const onPress = (returned: boolean) => {
    const options = ['Edit', 'Cancel']

    if (!returned) {
      options.unshift('Mark as Returned')
    }

    if (isUserAdmin) {
      // Insert "Delete" just before the trailing "Cancel" option.
      options.splice(options.length - 1, 0, 'Delete')
    }

    const editButtonIndex = options.findIndex((option) => option === 'Edit')
    const markReturnedButton = options.findIndex(
      (option) => option === 'Mark as Returned'
    )
    const deleteButtonIndex = options.findIndex((option) => option === 'Delete')
    const cancelButtonIndex = options.findIndex((option) => option === 'Cancel')

    showActionSheetWithOptions(
      {
        anchor: findNodeHandle(_anchorRef.current) || undefined,
        useModal: false,
        options,
        cancelButtonIndex,
        destructiveButtonIndex:
          deleteButtonIndex === -1 ? undefined : deleteButtonIndex,
      },
      (selectedIndex: number | undefined) => {
        switch (selectedIndex) {
          case editButtonIndex:
            setIsEditingModalOpen(true)
            break

          case markReturnedButton:
            setIsReturnAdoptionModalOpen(true)
            break

          case deleteButtonIndex:
            handleDelete()
            break
        }
      }
    )
  }

  return (
    <>
      <AdoptionForm
        adoption={adoption}
        fosterId={fosterId}
        mutation={editAdoptionMutation}
        onClose={() => setIsEditingModalOpen(false)}
        open={isEditingModalOpen}
      />
      <ReturnAdoptionAction
        fosterId={fosterId}
        onClose={() => setIsReturnAdoptionModalOpen(false)}
        open={isReturnAdoptionModalOpen}
      />
      {isNot('xs') ? (
        <View style={[styles.pressable, styles.documentContainer]} {...props}>
          <UniText style={[styles.nameColumnFlex]} testID="adoptedBy-text">
            {adoption.adoptedByName}
          </UniText>
          <UniText
            style={[styles.columnFlex, styles.columnContent]}
            testID="adoptedName-text"
          >
            {adoption.adoptedName}
          </UniText>
          <UniText
            style={[styles.columnFlex, styles.columnContent]}
            testID="adoptionDate-text"
          >
            {adoption.adoptionDate?.toDateString()}
          </UniText>
          <UniText
            style={[styles.columnFlex, styles.columnContent]}
            testID="returnDate-text"
          >
            {adoption.returnDate?.toDateString()}
          </UniText>
          <View style={styles.menuButtonContainer}>
            <Menu
              anchor={
                <UniIconButton
                  disabled={disabled}
                  icon="dots-vertical"
                  onPress={() => setMenuVisible(true)}
                  selected={menuVisible}
                  testID="adoption-menu-button"
                />
              }
              anchorPosition="bottom"
              onDismiss={() => setMenuVisible(false)}
              visible={menuVisible}
            >
              <Menu.Item
                leadingIcon="pencil"
                onPress={() => {
                  setMenuVisible(false)
                  setIsEditingModalOpen(true)
                }}
                title="Edit"
              />
              {!adoption.returnDate && (
                <Menu.Item
                  leadingIcon="undo"
                  onPress={() => {
                    setMenuVisible(false)
                    setIsReturnAdoptionModalOpen(true)
                  }}
                  title="Mark as Returned"
                />
              )}
              {isUserAdmin && (
                <Menu.Item
                  leadingIcon="delete"
                  onPress={() => {
                    setMenuVisible(false)
                    handleDelete()
                  }}
                  testID="adoption-delete-menu-item"
                  title="Delete"
                  titleStyle={{ color: theme.colors.error }}
                />
              )}
            </Menu>
          </View>
        </View>
      ) : (
        <UniTouchableRipple
          accessibilityRole="button"
          borderless
          disabled={disabled}
          onPress={() => onPress(!!adoption.returnDate)}
          ref={_anchorRef}
          style={styles.pressable}
          {...props}
        >
          <View style={styles.documentContainer}>
            <View style={styles.adoptionRow}>
              <View style={styles.flex1}>
                <UniText
                  style={styles.adoptedByName}
                  testID="adoptedByName-text"
                >
                  Adopted by {adoption.adoptedByName}
                </UniText>
                <UniText testID="adoptionDate-text">
                  Adoption Date: {adoption.adoptionDate?.toLocaleDateString()}
                </UniText>
                {!!adoption.adoptedName && (
                  <UniText
                    style={[styles.nameColumnFlex]}
                    testID="adoptedName-text"
                  >
                    Renamed to {adoption.adoptedName}
                  </UniText>
                )}
                <UniText style={styles.columnContent}>
                  Adopter's Email: {adoption.adoptedByEmail}
                </UniText>
                {!!adoption.adoptedByPhone && (
                  <UniText style={styles.columnContent}>
                    Adopter's Phone:{' '}
                    {formatPhoneNumber(adoption.adoptedByPhone)}
                  </UniText>
                )}
                {adoption.returnDate && (
                  <UniText
                    style={[styles.columnFlex, styles.columnContent]}
                    testID="returnDate-text"
                  >
                    Return Date: {adoption.returnDate?.toLocaleDateString()}
                  </UniText>
                )}
                {adoption.returnReason && (
                  <UniText style={styles.columnContent}>
                    Return Reason: {adoption.returnReason}
                  </UniText>
                )}
              </View>
              <UniIconButton
                disabled={disabled}
                icon="dots-vertical"
                onPress={() => onPress(!!adoption.returnDate)}
                ref={_anchorRef}
                selected
              />
            </View>
          </View>
        </UniTouchableRipple>
      )}
    </>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    adoptedByName: {
      fontWeight: 'bold',
    },
    adoptionRow: {
      alignSelf: 'stretch',
      alignItems: 'flex-start',
      display: 'flex',
      flex: 1,
      flexDirection: 'row',
      gap: 3,
    },
    columnContent: {
      color: {
        xs: theme.colors.subtitle,
        sm: theme.colors.onBackground,
      },
      fontSize: {
        xs: 12,
        sm: 14,
      },
    },
    columnFlex: {
      flex: {
        xs: 1,
        sm: 0.4,
      },
    },
    documentContainer: {
      alignItems: {
        xs: 'flex-start',
        sm: 'center',
      },
      flexDirection: {
        xs: 'column',
        sm: 'row',
      },
      gap: {
        xs: theme.tokens.spacing[1],
        sm: 0,
      },
      height: {
        sm: 40,
      },
      padding: 8,
      flex: 1,
    },
    flex1: {
      flex: 1,
    },
    menuButtonContainer: {
      flex: 0,
      minWidth: 40,
    },
    nameColumnFlex: {
      flex: {
        xs: 1,
        sm: 0.3,
      },
    },
    pressable: {
      borderRadius: theme.tokens.containerBorderRadius,
    },
  }
})

export default AdoptionListItem
